import type { InvoiceStatus } from '@/shared/api/types'

/**
 * В каком состоянии страница оплаты.
 *
 * Отдельная функция, а не набор `status === ...` по компонентам:
 * от этого решения зависит, показан ли на экране адрес. Разложенное
 * по трём местам, оно однажды разойдётся, и адрес останется на
 * оплаченной заявке — а плательщик, вернувшийся посмотреть статус,
 * заплатит второй раз. Это происходит регулярно и деньги не
 * возвращаются: адрес уходит обратно в пул и повторный перевод
 * не привязывается ни к какому заказу.
 */
export type Phase =
  /** Валюта ещё не выбрана — адреса нет. */
  | 'choose'
  /** Ждём перевод: адрес, сумма, QR. */
  | 'awaiting'
  /** Пришло меньше нужного, заявка ещё открыта: адрес и ОСТАТОК. */
  | 'topup'
  /** Деньги в сети, ждём подтверждений. Адреса нет. */
  | 'confirming'
  /** Всё кончено — оплачено, недоплачено насовсем или просрочено. Адреса нет. */
  | 'settled'

export function phaseOf(status: InvoiceStatus, hasPayment: boolean): Phase {
  switch (status) {
    // `pending` на бекенде означает ровно «валюта не выбрана», и адреса
    // при нём быть не может. Если он всё-таки пришёл — это рассогласование
    // на сервере, и показывать такой адрес нельзя: заявка ещё не привязана
    // к сети, и перевод на него никто не ждёт и не зачислит.
    case 'pending':
      return 'choose'
    case 'waiting_payment':
      return hasPayment ? 'awaiting' : 'choose'
    case 'underpaid_waiting':
      return 'topup'
    case 'confirming':
      return 'confirming'
    case 'paid':
    case 'overpaid':
    case 'underpaid':
    case 'expired':
      return 'settled'
  }
}

/** Показывать ли адрес получателя. Единственное место, где это решается. */
export function showsAddress(phase: Phase): boolean {
  return phase === 'awaiting' || phase === 'topup'
}
